import React from "react";
import { useTheme } from "@mui/material";
import { FiCheck, FiCpu } from "react-icons/fi";
import { Pill } from "./subscriptionUi";
import { addonPriceLabel, featureLabel, formatCents, isShownFeature, limitPhrase, METRICS, monthlyPrice } from "../../utils/subscriptionData";

// One plan as a card. Every number, feature and add-on price is read from the plan object (GET /plans).
const PlanCard = ({ plan, isCurrent = false, action }) => {
  const theme = useTheme();
  const price = monthlyPrice(plan);
  const credits = plan.addons?.ai_credit;
  const hasCredits = "ai_credits" in (plan.limits || {});
  const features = Object.keys(plan.features || {}).filter((key) => plan.features[key] && isShownFeature(key));
  const addons = Object.entries(plan.addons || {});

  return (
    <li
      className="flex flex-col rounded-2xl border bg-white p-5"
      style={{
        borderColor: isCurrent ? theme.palette.info.main : theme.palette.secondary.v4,
        boxShadow: isCurrent ? `0 0 0 2px ${theme.palette.info.main}, 0 10px 24px rgba(34,82,165,0.12)` : undefined,
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-lg font-semibold">{plan.name}</h3>
        {isCurrent && <Pill tone="blue">Current plan</Pill>}
      </div>
      {plan.description && <p className="text-sm text-accent-gray-light mt-1 min-h-[2.5rem]">{plan.description}</p>}

      <p className="mt-3 flex items-baseline gap-1">
        <span className="text-4xl font-bold tracking-tight">{price ? formatCents(price.amount_cents, price.currency) : "—"}</span>
        <span className="text-sm text-accent-gray-light">/ month</span>
      </p>

      <div className="mt-4">{action}</div>

      {hasCredits && (
        <div className="mt-4 flex items-center gap-3 rounded-xl bg-[#f3f6fb] p-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border bg-white text-accent-blue" aria-hidden="true">
            <FiCpu />
          </span>
          <div className="text-sm leading-tight">
            <p className="font-semibold">{limitPhrase(plan, "ai_credits")}</p>
            {credits && (
              <p className="text-xs text-accent-gray-light mt-0.5">Extra credits {formatCents(Math.round(credits.amount_cents / (credits.units || 1)))} each</p>
            )}
          </div>
        </div>
      )}

      <ul className="mt-4 space-y-2 text-sm">
        {METRICS.filter((metric) => metric.key !== "ai_credits" && metric.key in (plan.limits || {})).map((metric) => (
          <li key={metric.key} className="flex items-start gap-2">
            <FiCheck className="mt-0.5 shrink-0 text-accent-blue" aria-hidden="true" />
            <span>{limitPhrase(plan, metric.key)}</span>
          </li>
        ))}
        {features.map((key) => (
          <li key={key} className="flex items-start gap-2">
            <FiCheck className="mt-0.5 shrink-0 text-accent-blue" aria-hidden="true" />
            <span>{featureLabel(key)}</span>
          </li>
        ))}
      </ul>

      {addons.length > 0 && (
        <div className="mt-4 border-t pt-3" style={{ borderColor: theme.palette.secondary.v4 }}>
          <p className="text-xs font-semibold uppercase tracking-wide text-accent-gray-light">Add-ons</p>
          <ul className="mt-2 space-y-1 text-xs">
            {addons.map(([type, addon]) => (
              <li key={type} className="flex justify-between gap-3">
                <span>{addon.name}</span>
                <span className="shrink-0 text-accent-gray-light">{addonPriceLabel(addon)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
};

export default PlanCard;
